import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import apiClient, { setCsrfToken } from "../utils/apiClient.js";

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const applySession = (data) => {
    setCsrfToken(data.csrfToken);
    setAdmin({
      id: data.id,
      name: data.name,
      email: data.email,
      role: data.role,
      twoFactorEnabled: Boolean(data.twoFactorEnabled),
    });
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/session");
      applySession(data);
    } catch (err) {
      setCsrfToken(null);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    if (data.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }
    applySession(data);
    return { requiresTwoFactor: false };
  };

  const verifyTwoFactor = async (code) => {
    const { data } = await apiClient.post("/auth/login/2fa", { code });
    applySession(data);
    return data;
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } finally {
      setCsrfToken(null);
      setAdmin(null);
    }
  };

  return (
    <AdminContext.Provider value={{ admin, loading, login, verifyTwoFactor, logout, refresh }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);

export default AdminContext;
